import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	Output,
} from "@angular/core";

/**
 * Checkbox del design-system `.ds`, sin Material. Presentacional (no CVA), como
 * `app-ds-radio`: el contenedor pasa `[checked]` y reacciona a `(checkedChange)`.
 * La etiqueta va por `ng-content` (admite contenido rico). Sustituye a `mat-checkbox`.
 */
@Component({
	selector: "app-ds-checkbox",
	standalone: true,
	imports: [CommonModule, MatIconModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<label class="ds-check" [class.disabled]="disabled" [class.indeterminate]="indeterminate && !checked">
			<input
				type="checkbox"
				[checked]="checked"
				[disabled]="disabled"
				(change)="onToggle()"
			/>
			<span class="ds-check-box" aria-hidden="true">
				<mat-icon *ngIf="checked" [inline]="true">check</mat-icon>
					<mat-icon *ngIf="!checked && indeterminate" [inline]="true">remove</mat-icon>
			</span>
			<span class="ds-check-label"><ng-content></ng-content></span>
		</label>
	`,
	styles: [
		`
			.ds-check {
				display: inline-flex;
				align-items: center;
				gap: 9px;
				cursor: pointer;
			}
			.ds-check.disabled {
				cursor: not-allowed;
				opacity: 0.6;
			}
			.ds-check input {
				position: absolute;
				opacity: 0;
				width: 0;
				height: 0;
			}
			.ds-check-box {
				flex: none;
				width: 18px;
				height: 18px;
				border-radius: 5px;
				border: 2px solid var(--line);
				background: var(--paper);
				display: inline-flex;
				align-items: center;
				justify-content: center;
				color: #fff;
				font-size: 13px;
				transition:
					border-color 0.15s,
					background 0.15s,
					box-shadow 0.15s;
			}
			.ds-check input:checked + .ds-check-box,
				.ds-check.indeterminate .ds-check-box {
				border-color: var(--accent);
				background: var(--accent);
			}
			.ds-check input:focus-visible + .ds-check-box {
				box-shadow: 0 0 0 3px var(--accent-soft);
			}
			.ds-check-label {
				font-size: 14px;
				color: var(--ink);
			}
		`,
	],
})
export class DsCheckboxComponent {
	@Input() checked = false;
	@Input() disabled = false;
	/** Estado intermedio (tri-estado): muestra un guion y caja rellena sin estar "checked". */
	@Input() indeterminate = false;
	@Output() checkedChange = new EventEmitter<boolean>();

	onToggle(): void {
		if (!this.disabled) {
			this.checkedChange.emit(!this.checked);
		}
	}
}
